import React, { useState, useEffect, useCallback } from "react";
import Loader from "../../components/Loader/Loader";
import { tableConstants } from "./TeacherTimeTableConstant";
import Table from "../../components/Table/Table";
import Pagination from "../../components/Pagination/Pagination";
import { enqueueSnackbar } from "notistack";
import { getUsers } from "../../services/UserService";
import { updateTeacherTimeTableStatusBulk } from "../../services/TeacherSubjectMappingService";
import { uploadTeacherTimeTable } from "../../services/UserService";
import Input from "../../components/Input/Input";
import { FaCalendarAlt, FaEye, FaEyeSlash, FaFileUpload } from "react-icons/fa";
import { handleApiError } from "../../utils/errorHandler";
import "./TeacherTimeTable.css";
import Button from "../../components/Button/Button";

const TeacherTimeTableList = () => {
  const [name, setName] = useState("");
  const [timetables, setTimetables] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState({});

  const [data, setData] = useState([]);
  const [totalPage, setTotalPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loader, setLoader] = useState(false);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [savingTeacherId, setSavingTeacherId] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [togglingTeacherId, setTogglingTeacherId] = useState(null);
  const [bulkStatusUpdating, setBulkStatusUpdating] = useState(false);

  const fetchUserList = useCallback(async (name, page) => {
    try {
      setLoader(true);
      setCurrentPage(page);
      const response = await getUsers({
        name,
        page,
        includeTimetable: true,
        pageSize: 100,
      });
      if (response.success) {
        setData(response.data);
        setTotalPage(response.last_page);
        //Pre-select teachers whose timetable is already visible (status === 1)
        const preSelected = response.data
          .filter((item) => Number(item.timetable_status) === 1)
          .map((item) => item.id.toString());
        setSelectedTeacherIds(preSelected);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("organization_id")) {
      enqueueSnackbar("Select any organization!", { variant: "error" });
    }
    fetchUserList("", 1);
  }, [fetchUserList]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (name.length > 2 || name.length === 0) {
        fetchUserList(name, 1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [name, fetchUserList]);

  const handleInputChange = (e) => {
    const { value } = e.target;
    setName(value);
  };

  const handlePageChange = (i) => {
    const page = i.selected + 1;
    fetchUserList(name, page);
  };

  const uploadHandler = async (e, teacher_id) => {
    const file = e.target.files[0];
    if (file) {
      // Store in a state array like:
      setTimetables((prev) => {
        // Remove any existing entry for this teacher
        const filtered = prev.filter((f) => f.teacher_id !== teacher_id);
        return [...filtered, { teacher_id, timetableFile: file }];
      });
    }
    if (file) {
      setSelectedFiles((prev) => ({
        ...prev,
        [teacher_id]: file,
      }));
    }
  };

  const clearSelectedFileHandler = (teacher_id) => {
    setTimetables((prev) => prev.filter((entry) => entry.teacher_id !== teacher_id));
    setSelectedFiles((prev) => {
      const updated = { ...prev };
      delete updated[teacher_id];
      return updated;
    });
  };

  const uploadSingleFileHandler = async (e, teacher_id) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      const data = timetables.find((entry) => entry.teacher_id === teacher_id);
      if (data) {
        formData.append("timetables[0][teacher_id]", data.teacher_id);
        formData.append("timetables[0][timetable]", data.timetableFile);
      } else {
        enqueueSnackbar("No file selected for this teacher.", {
          variant: "warning",
        });
        return;
      }

      setSavingTeacherId(teacher_id);
      const response = await uploadTeacherTimeTable(formData);

      if (response.status === 200) {
        enqueueSnackbar("Timetable uploaded successfully!", {
          variant: "success",
        });

        //Remove the uploaded teacher's entry from timetables
        setTimetables((prev) =>
          prev.filter((entry) => entry.teacher_id !== teacher_id),
        );

        //Remove from selectedFiles if you're also maintaining it
        setSelectedFiles((prev) => {
          const updated = { ...prev };
          delete updated[teacher_id];
          return updated;
        });

        // Optionally, refresh the user list if needed
        fetchUserList(name, currentPage);
      } else {
        enqueueSnackbar("Some errors occurred during upload.", {
          variant: "warning",
        });
      }
    } catch (error) {
      console.error("Upload Error:", error);
      handleApiError(error);
    } finally {
      setSavingTeacherId(null);
    }
  };

  const bulkUploadHandler = async (timetablesArray) => {
    if (!timetablesArray.length) {
      enqueueSnackbar("Please select at least one timetable file to upload.", {
        variant: "warning",
      });
      return;
    }
    try {
      setBulkUploading(true);
      // Create a FormData object
      const formData = new FormData();
      // Append each timetable entry
      timetablesArray.forEach((entry, index) => {
        formData.append(`timetables[${index}][teacher_id]`, entry.teacher_id);
        formData.append(`timetables[${index}][timetable]`, entry.timetableFile); // Ensure file
      });
      // Call your API function
      const response = await uploadTeacherTimeTable(formData);

      if (response.status === 200) {
        enqueueSnackbar("Bulk Timetables uploaded successfully!", {
          variant: "success",
        });
        // Clear the timetables state after successful upload
        setTimetables([]);
        setSelectedFiles({});
        fetchUserList(name, currentPage); // Refresh list if needed
      } else {
        enqueueSnackbar("Some errors occurred during upload.", {
          variant: "warning",
        });
      }
    } catch (error) {
      console.error("Bulk Upload Error:", error);
      handleApiError(error);
    } finally {
      setBulkUploading(false);
    }
  };

  const isAllSelected =
    data.length > 0 && selectedTeacherIds.length === data.length;

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTeacherIds(data.map((item) => item.id.toString()));
    } else {
      setSelectedTeacherIds([]);
    }
  };

  const handleTeacherSelect = (event) => {
    const { value, checked } = event.target;
    if (checked) {
      setSelectedTeacherIds((prev) => [...prev, value]);
    } else {
      setSelectedTeacherIds((prev) => prev.filter((id) => id !== value));
    }
  };

  const singleStatusToggleHandler = async (teacherId, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    const label = newStatus === 1 ? "show" : "hide";
    if (
      !window.confirm(
        `Are you sure you want to ${label} timetable for this teacher?`,
      )
    )
      return;
    try {
      setTogglingTeacherId(teacherId);
      const response = await updateTeacherTimeTableStatusBulk({
        teacher_ids: [teacherId.toString()],
        status: newStatus,
      });
      if (response.status === 200) {
        enqueueSnackbar(response.data?.message?.success || "Status updated.", {
          variant: "success",
        });
        fetchUserList(name, currentPage);
      }
    } catch (error) {
      console.error(error);
      handleApiError(error);
    } finally {
      setTogglingTeacherId(null);
    }
  };

  const bulkUpdateStatusHandler = async (status) => {
    if (!selectedTeacherIds.length) {
      enqueueSnackbar("Please select at least one teacher.", {
        variant: "warning",
      });
      return;
    }
    const label = status === 1 ? "show" : "hide";
    if (
      !window.confirm(
        `Are you sure you want to ${label} timetable for ${selectedTeacherIds.length} teacher(s)?`,
      )
    )
      return;
    try {
      setBulkStatusUpdating(true);
      const response = await updateTeacherTimeTableStatusBulk({
        teacher_ids: selectedTeacherIds,
        status,
      });
      if (response.status === 200) {
        enqueueSnackbar("Timetable status updated successfully.", {
          variant: "success",
        });
        setSelectedTeacherIds([]);
        fetchUserList(name, currentPage);
      }
    } catch (error) {
      console.error(error);
      handleApiError(error);
    } finally {
      setBulkStatusUpdating(false);
    }
  };

  return (
    <React.Fragment>
      {loader ? (
        <Loader />
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="main-title-page page-title d-flex justify-content-between align-items-center mt-2">
              <div className="tt-header-title">
                <FaCalendarAlt />
                <span>Teacher TimeTable</span>
              </div>
              <div className="tt-search-wrap">
                <Input
                  type="text"
                  value={name}
                  placeholder="Search by name..."
                  onChangeHandler={handleInputChange}
                  autoFocus
                />
              </div>
            </div>
          </div>
          <div className="card-body evaluating-table">
            {data && data.length ? (
              <div>
                <Table
                  cols={tableConstants(
                    uploadHandler,
                    uploadSingleFileHandler,
                    selectedFiles,
                    currentPage,
                    isAllSelected,
                    selectedTeacherIds,
                    handleSelectAll,
                    handleTeacherSelect,
                    singleStatusToggleHandler,
                    savingTeacherId,
                    togglingTeacherId,
                    clearSelectedFileHandler,
                  )}
                  data={data}
                  hoverable={true}
                  striped={true}
                />
                <div className="tt-bulk-actions">
                  <span className="tt-bulk-actions-label">
                    {selectedTeacherIds.length
                      ? `${selectedTeacherIds.length} teacher(s) selected`
                      : "Select teachers to show/hide their timetable"}
                  </span>
                  <div className="tt-bulk-actions-buttons">
                    <Button
                      type="submit"
                      variant="success"
                      className="tt-btn"
                      disabled={!selectedTeacherIds.length || bulkStatusUpdating}
                      onClick={() => bulkUpdateStatusHandler(1)}
                    >
                      {bulkStatusUpdating ? (
                        <span className="tt-spinner" />
                      ) : (
                        <FaEye />
                      )}
                      Show Timetable
                    </Button>
                    <Button
                      type="submit"
                      variant="secondary"
                      className="tt-btn"
                      disabled={!selectedTeacherIds.length || bulkStatusUpdating}
                      onClick={() => bulkUpdateStatusHandler(0)}
                    >
                      {bulkStatusUpdating ? (
                        <span className="tt-spinner" />
                      ) : (
                        <FaEyeSlash />
                      )}
                      Hide Timetable
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="tt-btn"
                      disabled={!timetables.length || bulkUploading}
                      onClick={() => bulkUploadHandler(timetables)}
                    >
                      {bulkUploading ? (
                        <span className="tt-spinner" />
                      ) : (
                        <FaFileUpload />
                      )}
                      Upload All
                      {timetables.length ? (
                        <span className="tt-count-badge">{timetables.length}</span>
                      ) : null}
                    </Button>
                  </div>
                </div>
                <Pagination
                  totalPage={totalPage}
                  currentPage={currentPage - 1}
                  handlePageChange={(i) => handlePageChange(i)}
                ></Pagination>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default TeacherTimeTableList;
